import { MessageBarType } from '@fluentui/react';
import { KeyValuePairSubmitOutcome, useToast } from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useEngine } from '../../../../../aiem/engine/hooks';
import { AIEMOpType, AIEngine } from '../../../../../aiem/engine/types';
import { Engine_Type } from '../../../../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { H2OEngineDefaultProfiles, H2OEngineProfileOptionKeyType } from '../../../constants';
import { useEngineProfiles } from './useEngineProfiles';
import { useEngineValidation } from './useEngineValidation';
import { useEngineVersions } from './useEngineVersions';

/**
 * Main orchestrating hook that combines all engine configuration functionality.
 *
 * This is the primary hook for the EngineConfiguration component that coordinates
 * between all specialized hooks to provide a complete engine configuration experience.
 *
 * Responsibilities:
 * - Manages engine state and lifecycle throughout the configuration process
 * - Handles save operations and toast notifications for success/failure states
 * - Coordinates between different hook functionalities (versions, profiles, validation)
 * - Combines useEngineVersions, useEngineProfiles, and useEngineValidation hooks
 * - Provides unified interface for the EngineConfiguration component
 * - Manages loading states and error handling
 *
 * @param op - The operation type (create, update, view)
 * @param engine - The engine being configured
 * @returns Comprehensive object with all state, handlers, and utilities needed by the component
 */
export const useEngineConfiguration = (op: AIEMOpType, engine: AIEngine) => {
  const { opOnEngine } = useEngine();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [engineState, setEngineState] = useState<AIEngine>({ ...engine });
  const advancedConfigurationRef = useRef<any>();
  const waitingForEngineStateUpdate = useRef<boolean>(false);

  const engineType = engine?.engineType;
  const isNotebookEngine = engineType === Engine_Type.NOTEBOOK;

  // Initialize hooks
  const versionsHook = useEngineVersions(engine, op);
  const profilesHook = useEngineProfiles(engine, op);
  const validationHook = useEngineValidation(
    engineState,
    profilesHook.selectedProfileOption,
    versionsHook.versionKey,
    op
  );

  const isH2ODefaultProfileSelected =
    profilesHook.selectedConfigurationOption.key === H2OEngineProfileOptionKeyType.compressed ||
    profilesHook.selectedConfigurationOption.key === H2OEngineProfileOptionKeyType.raw;

  const isNewOrUpdated = useCallback((): boolean => {
    if (op === AIEMOpType.create) {
      return true;
    }
    const { profileInfo: engineProfileInfo, ...restEngine } = engine;
    const { profileInfo, ...restEngineState } = engineState;
    return JSON.stringify(restEngine) !== JSON.stringify(restEngineState);
  }, [engine, engineState, op]);

  const updateEngine = useCallback((eng: Partial<AIEngine>) => {
    setEngineState((prev) => ({ ...prev, ...eng }));
  }, []);

  const createSuccessToast = useCallback(
    (displayName: string, operation: AIEMOpType) => {
      addToast({
        messageBarType: MessageBarType.success,
        message:
          operation === AIEMOpType.create
            ? `Engine "${displayName}" has been successfully created and it is now starting.`
            : `Engine "${displayName}" has been successfully updated.`,
      });
    },
    [addToast]
  );

  const saveEngine = useCallback(
    async (onSave: () => any, onCancel: () => any) => {
      const stateCopy = { ...engineState };

      // Clear readonly fields before the submission
      delete stateCopy['notebookImageInfo'];

      const operation = op === AIEMOpType.create ? AIEMOpType.create : AIEMOpType.update;
      const res = await opOnEngine(stateCopy, operation, undefined, undefined);

      if (res) {
        onSave();
        createSuccessToast(String(engineState!.displayName), operation);
      } else {
        onCancel();
      }
    },
    [op, engineState, opOnEngine, createSuccessToast]
  );

  const prepareConfiguration = useCallback(async () => {
    setIsLoading(true);
    try {
      const loadedSet = await profilesHook.fetchProfileOptions();
      if (!loadedSet) return;

      const defaultEngine = profilesHook.getEngineFromConstraintSet(loadedSet);

      if (op === AIEMOpType.create && engine.type !== Engine_Type.NOTEBOOK) {
        const defaultVersion = await versionsHook.loadVersions();
        updateEngine({
          ...defaultEngine,
          profile: loadedSet.name,
          [versionsHook.versionKey]: defaultVersion?.key,
          [versionsHook.versionInfoKey]: defaultVersion,
        });
      } else if (op === AIEMOpType.edit) {
        await versionsHook.loadVersions();
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  }, [op, engine.type, updateEngine]);

  const handleVersionChange = useCallback((e: any, option: any) => {
    versionsHook.handleVersionChange(e, option, updateEngine);
  }, []);

  const handleProfileOptionSelect = useCallback((_: any, option: any) => {
    profilesHook.handleProfileOptionSelect(_, option);
    // Also reset configuration to default H2O profile
    profilesHook.handleConfigurationOptionSelect(undefined, H2OEngineDefaultProfiles[0]);
  }, []);

  const handleConfigurationOptionSelect = useCallback((_: any, option: any) => {
    profilesHook.handleConfigurationOptionSelect(_, option);
    if (profilesHook.selectedProfileOption) {
      updateEngine({
        ...profilesHook.getEngineFromConstraintSet(profilesHook.selectedProfileOption),
      });
    }
  }, []);

  const onClickSave = useCallback(
    (onSave: () => any, onCancel: () => any) => {
      if (advancedConfigurationRef.current) {
        waitingForEngineStateUpdate.current = true;
        const saveOutcome = advancedConfigurationRef.current.submitKeyValue();

        switch (saveOutcome) {
          case KeyValuePairSubmitOutcome.UNSUCCESSFUL:
            waitingForEngineStateUpdate.current = false;
            saveEngine(onSave, onCancel);
            break;
          case KeyValuePairSubmitOutcome.SUCCESSFUL:
            waitingForEngineStateUpdate.current = true;
            break;
          case KeyValuePairSubmitOutcome.INVALID:
            waitingForEngineStateUpdate.current = false;
            break;
          default:
            saveEngine(onSave, onCancel);
        }
      } else {
        void saveEngine(onSave, onCancel);
      }
    },
    [saveEngine]
  );

  // Effects
  useEffect(() => {
    if (waitingForEngineStateUpdate.current) {
      waitingForEngineStateUpdate.current = false;
      // Note: This would need the callback functions passed in
      // void saveEngine(onSave, onCancel);
    }
  }, [engineState]);

  useEffect(() => {
    if (profilesHook.selectedProfileOption && op === AIEMOpType.create) {
      const newEngineState = {
        ...profilesHook.getEngineFromConstraintSet(profilesHook.selectedProfileOption),
        ...(!isH2ODefaultProfileSelected ? { profile: profilesHook.selectedProfileOption.name } : {}),
      } as AIEngine;
      updateEngine(newEngineState);
    }

    if (profilesHook.selectedProfileOption && op !== AIEMOpType.create) {
      const newEngineState = {
        profile: profilesHook.selectedProfileOption.name,
        profileInfo: undefined,
        ...profilesHook.getEngineFromConstraintSet(profilesHook.selectedProfileOption),
      } as AIEngine;
      updateEngine(newEngineState);
    }
  }, [profilesHook.selectedProfileOption, op, isH2ODefaultProfileSelected, updateEngine]);

  useEffect(() => {
    void prepareConfiguration();
  }, [prepareConfiguration]);

  return {
    // State
    isLoading,
    engineState,

    // Refs
    advancedConfigurationRef,

    // Computed values
    isNotebookEngine,
    isH2ODefaultProfileSelected,

    // Functions
    updateEngine,
    onClickSave,

    // Validation
    isEngineValid: validationHook.isEngineValid,
    isConfigEditabilityDisabled: validationHook.isConfigEditabilityDisabled,
    validateAdvancedConfiguration: validationHook.validateAdvancedConfiguration,
    isNewOrUpdated,

    // Versions
    selectedVersion: versionsHook.selectedVersion,
    versions: versionsHook.versions,
    handleVersionChange,
    isVersionInList: versionsHook.isVersionInList,

    // Profiles
    selectedProfileOption: profilesHook.selectedProfileOption,
    profileOptions: profilesHook.profileOptions,
    selectedConfigurationOption: profilesHook.selectedConfigurationOption,
    handleProfileOptionSelect,
    handleConfigurationOptionSelect,
    isProfileInList: profilesHook.isProfileInList,
    isDAIEngine: profilesHook.isDAIEngine,
  };
};
